import { Link } from "react-router-dom";
import { Menu, X, GraduationCap, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useBranding } from "@/context/BrandingContext";

export function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [partnerMenuOpen, setPartnerMenuOpen] = useState(false);
    const partnerMenuRef = useRef<HTMLDivElement>(null);
    const { branding, loading } = useBranding();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (partnerMenuRef.current && !partnerMenuRef.current.contains(e.target as Node)) {
                setPartnerMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const logoUrl = branding?.branding?.logo || "/eduhub-logo.png";
    const universityName = branding?.name || "University Portal";

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                isOpen
                    ? "bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)] py-3 border-b border-slate-100"
                    : scrolled
                    ? "bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] py-3 border-b border-slate-100"
                    : "bg-transparent py-6"
            }`}
        >
            <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
                <Link to="/" className={`flex items-center gap-4 ${isOpen ? 'hidden' : ''}`}>
                    <div className="relative overflow-hidden w-10 h-10 flex items-center justify-center">
                        {loading ? (
                            <div className="h-10 w-10 bg-slate-200 animate-pulse rounded"></div>
                        ) : branding?.branding?.logo ? (
                            <img
                                src={logoUrl}
                                alt={`${universityName} Logo`}
                                className="h-10 w-auto max-w-none object-cover object-left"
                                onError={(e) => {
                                    const img = e.target as HTMLImageElement;
                                    img.onerror = null; // Prevent infinite loop if fallback also fails
                                    img.src = "/eduhub-logo.png";
                                }}
                            />
                        ) : (
                            <div 
                                className="w-10 h-10 rounded flex items-center justify-center text-white"
                                style={{
                                    backgroundColor: branding?.branding?.colors?.primary || '#1d3658'
                                }}
                            >
                                <GraduationCap className="w-6 h-6" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <span
                            className={`text-xl font-black tracking-tight leading-none transition-colors ${
                                scrolled ? "text-slate-900" : "text-slate-900"
                            }`}
                            style={{
                                fontFamily: 'var(--font-university-name)'
                            }}
                        >
                            {universityName}
                        </span>
                        <span
                            className="text-[10px] uppercase tracking-[0.2em] font-bold"
                            style={{
                                color: branding?.branding?.colors?.accent || '#517dee'
                            }}
                        >
                            Application Portal
                        </span>
                    </div>
                </Link>

                {/* Desktop Nav */}
                <div className="hidden lg:flex items-center gap-10">
                    <div className="flex items-center gap-8">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            className={`text-xs uppercase tracking-widest font-bold transition-all relative group/link flex items-center h-10 ${
                                scrolled ? "text-slate-900" : "text-slate-900"
                            }`}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = branding?.branding?.colors?.accent || '#517dee';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = '';
                            }}
                        >
                            <span className="relative inline-block">
                                Home
                                <span 
                                    className="absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-300 group-hover/link:w-full"
                                    style={{
                                        backgroundColor: branding?.branding?.colors?.accent || '#517dee'
                                    }}
                                />
                            </span>
                        </a>
                        {["Programs"].map((item) => (
                            <a
                                key={item}
                                href="#programs"
                                className={`text-xs uppercase tracking-widest font-bold transition-all relative group/link flex items-center h-10 ${
                                    scrolled ? "text-slate-900" : "text-slate-900"
                                }`}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = branding?.branding?.colors?.accent || '#517dee';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '';
                                }}
                            >
                                <span className="relative inline-block">
                                    {item}
                                    <span
                                        className="absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-300 group-hover/link:w-full"
                                        style={{
                                            backgroundColor: branding?.branding?.colors?.accent || '#517dee'
                                        }}
                                    />
                                </span>
                            </a>
                        ))}

                        <div ref={partnerMenuRef} className="relative flex items-center h-10">
                            <button
                                type="button"
                                onClick={() => setPartnerMenuOpen((o) => !o)}
                                className={`text-xs uppercase tracking-widest font-bold transition-all flex items-center gap-1 ${
                                    scrolled ? "text-slate-900" : "text-slate-900"
                                }`}
                                style={{ color: partnerMenuOpen ? (branding?.branding?.colors?.accent || '#517dee') : undefined }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = branding?.branding?.colors?.accent || '#517dee';
                                }}
                                onMouseLeave={(e) => {
                                    if (!partnerMenuOpen) e.currentTarget.style.color = '';
                                }}
                            >
                                Partner
                                <ChevronDown className={`w-3 h-3 transition-transform ${partnerMenuOpen ? "rotate-180" : ""}`} />
                            </button>
                            <AnimatePresence>
                                {partnerMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 6 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute top-full left-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50"
                                    >
                                        <Link
                                            to="/partner/agency-login"
                                            className="block px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                                            onClick={() => setPartnerMenuOpen(false)}
                                        >
                                            Partner Login
                                        </Link>
                                        <Link
                                            to="/partner/agency-register"
                                            className="block px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                                            onClick={() => setPartnerMenuOpen(false)}
                                        >
                                            Become Partner
                                        </Link>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="h-6 w-px bg-slate-300" />

                    <div className="flex items-center gap-3">
                        <Link to="/login">
                            <button className="text-xs uppercase tracking-widest font-bold px-6 py-2 text-slate-900 hover:bg-slate-100 rounded-full transition-all duration-300">
                                Login
                            </button>
                        </Link>
                        <Link to="/register">
                            <button 
                                className="text-white text-xs uppercase tracking-widest font-black px-8 h-11 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 hover:opacity-90"
                                style={{
                                    backgroundColor: branding?.branding?.colors?.primary || '#1d3658'
                                }}
                            >
                                Apply Now
                            </button>
                        </Link>
                    </div>
                </div>

                {/* Mobile Toggle */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`lg:hidden p-2 -mr-2 rounded-xl transition-all hover:bg-slate-100 text-slate-900 active:scale-95 ${isOpen ? 'ml-auto' : ''}`}
                    aria-label="Toggle menu"
                >
                    {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop overlay with blur - rendered via portal to escape nav stacking context */}
                        {createPortal(
                            <motion.div
                                key="mobile-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
                                onClick={() => setIsOpen(false)}
                            />,
                            document.body
                        )}
                        <motion.div
                            key="mobile-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="lg:hidden bg-white border-b border-slate-100 overflow-hidden shadow-xl relative z-50"
                        >
                        <div className="container px-4 py-6 flex flex-col gap-6">
                            <div className="py-4 border-t border-slate-50">
                                <a
                                    href="#"
                                    className="text-xl font-bold text-slate-900 py-2 block"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsOpen(false);
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                >
                                    Home
                                </a>
                                {["Programs"].map((item) => (
                                    <a
                                        key={item}
                                        href="#programs"
                                        className="text-xl font-bold text-slate-900 py-2 block"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {item}
                                    </a>
                                ))}
                                <div className="pt-1">
                                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Partner</p>
                                    <Link
                                        to="/partner/agency-login"
                                        className="text-xl font-bold text-slate-900 py-2 block"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        Partner Login
                                    </Link>
                                    <Link
                                        to="/partner/agency-register"
                                        className="text-xl font-bold text-slate-900 py-2 block"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        Become Partner
                                    </Link>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 pb-4">
                                <Link to="/login">
                                    <button
                                        className="w-full border border-slate-200 text-slate-900 font-bold rounded-full h-12 hover:bg-slate-50 transition-colors"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        Login
                                    </button>
                                </Link>
                                <Link to="/register">
                                    <button 
                                        className="w-full text-white font-bold rounded-full h-12 shadow-lg transition-opacity hover:opacity-90"
                                        style={{
                                            backgroundColor: branding?.branding?.colors?.primary || '#1d3658'
                                        }}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        Apply Now
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                    </>
                )}
            </AnimatePresence>
        </nav>
    );
}
